import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectsService } from '../../services/projects/projects.service';

export function useProjects(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['projects', 'list', params],
    queryFn: () => ProjectsService.list(params),
    enabled: false,
  });
}

export function useProjectDetails(id: string | number) {
  return useQuery({
    queryKey: ['projects', 'details', id],
    queryFn: () => ProjectsService.getById(id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => ProjectsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
    },
  });
}

export function useProjectDashboard(id: string | number) {
  return useQuery({
    queryKey: ['projects', 'dashboard', id],
    queryFn: () => ProjectsService.getDashboard(id),
    enabled: !!id,
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: any }) => ProjectsService.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'details', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', variables.id] });
    },
  });
}

export function useProjectMembers(projectId: string | number) {
  return useQuery({
    queryKey: ['projects', 'members', projectId],
    queryFn: () => ProjectsService.getMembers(projectId),
    enabled: !!projectId,
  });
}

export function useProjectSprints(projectId: string | number) {
  return useQuery({
    queryKey: ['projects', 'sprints', projectId],
    queryFn: () => ProjectsService.getSprints(projectId),
    enabled: !!projectId,
  });
}

export function useProjectMilestones(projectId: string | number) {
  return useQuery({
    queryKey: ['projects', 'milestones', projectId],
    queryFn: () => ProjectsService.getMilestones(projectId),
    enabled: !!projectId,
  });
}

export function useCreateProjectMember(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => ProjectsService.createMember(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'members', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useUpdateProjectMember(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, data }: { memberId: string; data: any }) =>
      ProjectsService.updateMember(projectId, memberId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'members', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useDeleteProjectMember(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => ProjectsService.deleteMember(projectId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'members', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useCreateProjectTask(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => ProjectsService.createTask(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'tasks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useDeleteProjectTask(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => ProjectsService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'tasks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useUpdateTaskStatus(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      ProjectsService.updateTaskStatus(taskId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'tasks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useProjectComments(projectId: string | number) {
  return useQuery({
    queryKey: ['projects', 'comments', projectId],
    queryFn: () => ProjectsService.getComments(projectId),
    enabled: !!projectId,
  });
}

export function useCreateProjectComment(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => ProjectsService.createComment(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'comments', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useDeleteProjectComment(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => ProjectsService.deleteComment(projectId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'comments', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useCreateProjectSprint(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => ProjectsService.createSprint(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'sprints', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useUpdateProjectSprint(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sprintId, data }: { sprintId: string; data: any }) =>
      ProjectsService.updateSprint(projectId, sprintId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'sprints', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useDeleteProjectSprint(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sprintId: string) => ProjectsService.deleteSprint(projectId, sprintId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'sprints', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useCreateProjectMilestone(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => ProjectsService.createMilestone(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'milestones', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useUpdateProjectMilestone(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, data }: { milestoneId: string; data: any }) =>
      ProjectsService.updateMilestone(projectId, milestoneId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'milestones', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useDeleteProjectMilestone(projectId: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) => ProjectsService.deleteMilestone(projectId, milestoneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'milestones', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'dashboard', projectId] });
    },
  });
}

export function useProjectAuditLogs(projectId: string | number) {
  return useQuery({
    queryKey: ['projects', 'audit-logs', projectId],
    queryFn: () => ProjectsService.getAuditLogs(projectId),
    enabled: !!projectId,
  });
}

export function useReactions(entityType: string, entityId: string | number) {
  return useQuery({
    queryKey: ['reactions', entityType, entityId],
    queryFn: () => ProjectsService.getReactions(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

export function useCreateReaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId, type }: { entityType: string; entityId: string | number; type: string }) =>
      ProjectsService.createReaction(entityType, entityId, type),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reactions', variables.entityType, variables.entityId] });
    },
  });
}

export function useDeleteReaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId, type }: { entityType: string; entityId: string | number; type: string }) =>
      ProjectsService.deleteReaction(entityType, entityId, type),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reactions', variables.entityType, variables.entityId] });
    },
  });
}

export function useProjectDocuments(projectId: string | number) {
  return useQuery({
    queryKey: ['projects', 'documents', projectId],
    queryFn: () => ProjectsService.getProjectDocuments(projectId),
    enabled: !!projectId,
  });
}

export function useProjectFinance(projectId: string | number) {
  return useQuery({
    queryKey: ['projects', 'finance', projectId],
    queryFn: () => ProjectsService.getProjectFinance(projectId),
    enabled: !!projectId,
  });
}
